import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { GameItem, handleUpdateItem } from "store/gameCenter/games/slice";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import Button from "components/buttons/Button";

import cn from "classnames";
import { ImageUpload } from "components/imageUpload";

interface GamesMainTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  canBeUpdated?: boolean;
}

type TBadgeCheckboxProps = Pick<GameItem, 'isExclusive' | 'isNew' | 'isPopular' | 'isTop' | 'isTrending' | 'isVip'>;

const POKER_PLANET_BADGE_LABELS: Partial<Record<keyof TBadgeCheckboxProps, string>> = {
  isVip: "Promo",
  isPopular: "Jackpot",
  isTrending: "Hot",
};

const GamesMainTab: React.FC<GamesMainTabProps> = ({
  fieldErrors,
  clearFieldError,
  canBeUpdated,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPokerPlanet = [
    "bo.planets.casino",
    "backoffice-staging.planets.casino",
  ].includes(window.location.hostname);
  const payload = useAppSelector((store) => store.games.item);
  const [description, setDescription] = useState<string>("");
  const [isDescriptionFetching, setIsDescriptionFetching] =
    useState<boolean>(false);

  const badgeCheckboxes: (keyof TBadgeCheckboxProps)[] = ['isVip', 'isTop', 'isNew', 'isPopular', 'isExclusive','isTrending'];

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateItem({ [key]: img ?? value }));
  };

  const handleAIText = async () => {
    setIsDescriptionFetching(true);
    try {
      const result = await instance.post(apiRoutes.generateAIText, {
        instanceType: "game",
        attribute: "description",
        instanceId: payload?.id,
      });
      if (!description) {
        setDescription(payload?.description ?? "empty");
      }
      handleData("description", result.data?.text ?? "");
    } catch {
      console.log("handleAIText error");
    }
    setIsDescriptionFetching(false);
  };

  const handleInit = () => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.gameSession}&entityId=${searchParams.get("entityId")}&game=${payload?.slug}&provider=${payload?.provider.slug}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.gameSession }));
  };

  const handleImageChange = (
    key: "portrait" | "landscape" | "square",
    file: File,
  ) => {
    dispatch(
      handleUpdateItem({
        images: {
          ...payload?.images,
          [key]: file,
        },
      }),
    );
  };

  // list of badge checkboxes
  const renderBadgeCheckbox = (
    badgeName: keyof TBadgeCheckboxProps,
    badgeValue: TBadgeCheckboxProps[keyof TBadgeCheckboxProps],
    onChange: (key: keyof TBadgeCheckboxProps, v: boolean) => void,
    disabled?: boolean,
  ) => {
    return (
      <div
      
        className={cn("item badge-checkbox", badgeValue && "is-checked", disabled && "disabled")}
        onClick={() => {
          if (disabled) return;

          onChange(badgeName, badgeValue ? false : true)
        }}
      >
        <div
          className={cn('def-check-control', badgeValue && 'checked')}
        />
        <div className="label">
          {isPokerPlanet
            ? POKER_PLANET_BADGE_LABELS[badgeName] ??
              t(badgeName.slice(2).toLowerCase())
            : t(badgeName.slice(2).toLowerCase())}
        </div>
      </div>
    )
  };

  if (!payload) return null;

  return (
    <div className={cn('game-form-wrapper edit-game-form', payload?.isActive && 'active')}>
      <div className="game-form-content">
        <div className="game-form-box">
          <div className="game-form-box--item">
            <InnerInput
              value={payload?.title ?? ""}
              placeholder={t("enterTitle")}
              label={t("title")}
              type="text"
              id="game-title"
              onChange={(e) => {
                handleData("title", e);
                clearFieldError("title");
              }}
              hasError={Boolean(fieldErrors?.["title"])}
              disabled={!canBeUpdated}
            />
          </div>
          <div className="game-form-box--item">
            <InnerInput
              value={payload?.slug ?? ""}
              placeholder={t("enterSlug")}
              label={t("slug")}
              type="text"
              id="game-slug"
              onChange={(e) => {
                handleData("slug", e);
                clearFieldError("slug");
              }}
              hasError={Boolean(fieldErrors?.["slug"])}
              disabled={!canBeUpdated}
            />
          </div>
          <div className="game-form-box--item">
            <InnerInput
              value={payload?.provider.title ?? ""}
              label={t("provider")}
              type="text"
              id="provider-slug"
              disabled
              onChange={(e) => {
                handleData("provider", e);
                clearFieldError("provider");
              }}
              hasError={Boolean(fieldErrors?.["provider"])}
              
            />
          </div>
          <div className="game-form-box--item">
            <InnerInput
              value={payload?.type ?? ""}
              placeholder={t("enterType")}
              label={t("type")}
              type="text"
              id="game-type"
              onChange={(e) => {
                handleData("type", e);
                clearFieldError("type");
              }}
              hasError={Boolean(fieldErrors?.["type"])}
              disabled={!canBeUpdated}
            />
          </div>
          <div className="game-form-box--item">
            <div className="game-form-box--item with-ai">
              <div className="ai-buttons">
                <Button
                  handleClick={() => {
                    handleData(
                      "description",
                      description === "empty" ? "" : description,
                    );
                    setDescription("");
                  }}
                  disabled={!description || isDescriptionFetching || !canBeUpdated}
                  isSecondary
                  txt="&larr;"
                />
                <Button
                  disabled={isDescriptionFetching || !canBeUpdated}
                  handleClick={handleAIText}
                  txt={t("aiAssist")}
                />
              </div>
              <InnerInput
                maxCodePoints={2500}
                value={payload?.description ?? ""}
                placeholder={t("enterText")}
                label={t("description")}
                type="textarea"
                id="game-description"
                disabled={isDescriptionFetching || !canBeUpdated}
                onChange={(e) => {
                  handleData("description", e);
                  clearFieldError("description");
                }}
                hasError={Boolean(fieldErrors?.["description"])}
              />
            </div>
          </div>
        </div>

        <div className="game-form-box">
          <div className="game-grid game-form-img game-form-img--game column">
            <div
              className={cn("game-item", {
                "has-error": fieldErrors?.["file"],
              })}
            >
              <ImageUpload
                image={payload?.image}
                isActive={Boolean(payload?.isActive)}
                size="large"
                onAddImage={(file) => {
                  handleData("image", "", file);
                  clearFieldError("file");
                }}
                disabled={!canBeUpdated}
              />

              <div
                className={cn("game-img-vip", {
                  checked: payload?.isVip,
                  "poker-planet": isPokerPlanet,
                })}
              />

              <div
                className={cn("game-img-top", {
                  checked: payload?.isTop,
                })}
              />

              <div
                className={cn("game-img-new", {
                  checked: payload?.isNew,
                })}
              />

              <div
                className={cn("game-img-popular", {
                  checked: payload?.isPopular,
                  "poker-planet": isPokerPlanet,
                })}
              />

              <div
                className={cn("game-img-exclusive", {
                  checked: payload?.isExclusive,
                })}
              />

              <div
                className={cn("game-img-trending", {
                  checked: payload?.isTrending,
                  "poker-planet": isPokerPlanet,
                })}
              />
            </div>

            <SwitchBtn
              disabled={!canBeUpdated}
              activeTxt={t("active")}
              notActiveTxt={t("hidden")}
              isActive={!!payload?.isActive}
              handleClick={() => {
                handleData(
                  "isActive",
                  payload?.isActive ? !payload?.isActive : true,
                );
                clearFieldError("isActive");
              }}
              hasError={Boolean(fieldErrors?.["isActive"])}
            />
          </div>
          <div className="game-form-box--item">
            <div className="game-image-slots">
              {" "}
              <div className="slot portrait">
                <ImageUpload
                  disabled={!canBeUpdated}
                  label={t("portrait")}
                  image={payload?.images?.portrait}
                  isActive={Boolean(payload?.isActive)}
                  onAddImage={(file) => handleImageChange("portrait", file)}
                  aspect="9:16"
                  type="portrait"
                />
              </div>
              <div className="slot landscape">
                <ImageUpload
                  disabled={!canBeUpdated}
                  label={t("landscape")}
                  image={payload?.images?.landscape}
                  isActive={Boolean(payload?.isActive)}
                  onAddImage={(file) => handleImageChange("landscape", file)}
                  aspect="16:9"
                  type="landscape"
                />
              </div>
              <div className="slot square">
                <ImageUpload
                  disabled={!canBeUpdated}
                  label={t("square")}
                  image={payload?.images?.square}
                  isActive={Boolean(payload?.isActive)}
                  onAddImage={(file) => handleImageChange("square", file)}
                  aspect="1:1"
                  type="square"
                />
              </div>
            </div>
          </div>

          <div className="play-btn-wrap row">
            <Button
              isBordered
              isGreen
              handleClick={handleInit}
              txt={t("play")}
            />
          </div>
        </div>
      </div>
      <div className="game-form-cheks">
        <div className="game-form-check--row">
          {badgeCheckboxes.map(badge => (
            <React.Fragment key={badge}>
              {renderBadgeCheckbox(badge, payload[badge], handleData, !canBeUpdated)}
            </React.Fragment>
          ))}
        </div>
        <div className="game-form-switch--row">
          <SwitchBtn
            disabled={!canBeUpdated}
            activeTxt={t("freespins")}
            isActive={!!payload?.hasFreeSpins}
            handleClick={() => {
              handleData(
                "hasFreeSpins",
                payload?.hasFreeSpins ? !payload?.hasFreeSpins : true,
              );
              clearFieldError("hasFreeSpins");
            }}
            hasError={Boolean(fieldErrors?.["hasFreeSpins"])}
          />
          <SwitchBtn
            disabled={!canBeUpdated}
            activeTxt={t("hasDemo")}
            isActive={!!payload?.hasDemo}
            handleClick={() => {
              handleData(
                "hasDemo",
                payload?.hasDemo ? !payload?.hasDemo : true,
              );
              clearFieldError("hasDemo");
            }}
            hasError={Boolean(fieldErrors?.["hasDemo"])}
          />
          <SwitchBtn
            disabled={!canBeUpdated}
            activeTxt={t("mobile")}
            isActive={!!payload?.isMobile}
            handleClick={() => {
              handleData(
                "isMobile",
                payload?.isMobile ? !payload?.isMobile : true,
              );
              clearFieldError("isMobile");
            }}
            hasError={Boolean(fieldErrors?.["isMobile"])}
          />
          <SwitchBtn
            disabled={!canBeUpdated}
            activeTxt={t("deactivated")}
            isActive={!!payload?.isDeactivated}
            isRed
            handleClick={() => {
              handleData(
                "isDeactivated",
                payload?.isDeactivated ? !payload?.isDeactivated : true,
              );
              clearFieldError("isDeactivated");
            }}
            hasError={Boolean(fieldErrors?.["isDeactivated"])}
          />
        </div>
      </div>
      <div className="game-form-info-list">
        <div className="item">
          <div className="label">{t("reelCount")}:</div>
          <div className="value">{payload?.reelsCount}</div>
        </div>
        <div className="item">
          <div className="label">{t("linesCount")}:</div>
          <div className="value">{payload?.linesCount}</div>
        </div>
        <div className="item">
          <div className="label">{t("technology")}:</div>
          <div className="value">{payload?.technology}</div>
        </div>
        <div className="item">
          <div className="label">RTP:</div>
          <div className="value">{payload?.rtp}</div>
        </div>
        <div className="item">
          <div className="label">{t("volatility")}:</div>
          <div className="value">{payload?.volatility}</div>
        </div>
      </div>
    </div>
  );
};

export default GamesMainTab;
